/**
 * 시공일정 패널. 공정별 「며칠」을 착공일 기준으로 펼쳐 날짜를 만든다.
 *
 * **규칙 엔진이 아니라 생성기다.** 한 번 펼치면 날짜가 진짜 데이터가 되고, 그
 * 뒤로는 담당자가 칸을 직접 고친다 — 공휴일 예외도, 겹치는 공정도 여기서 손으로
 * 해결한다. 그래서 겹침·순서 경고를 띄우지 않는다.
 *
 * 공정 줄은 **견적의 공종에서 파생된다.** 사람이 줄을 만들지 않고, 공종을 넣고
 * 빼면 줄도 따라 는다. 「무슨 작업인지」도 그 공정에 묶인 공종 이름으로 채운다.
 */

import { CSSProperties, Dispatch, useMemo, useState } from "react";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import InfoTip from "@buildpy/ui/info-tip";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { generateQuoteSchedule } from "@/api/actions/quote";
import NumberField from "@/modules/quote/ui/editor/number-field";
import { lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import {
  EditorAction,
  EditorPhasePlan,
  EditorSchedule,
  EditorTrade,
} from "@buildpy/core/modules/quote/editor/types";
import type { ProcessCalibration } from "@buildpy/core/modules/process-standard/calibration";
import { crewLabel, DraftLine, draftOfItem, draftPhase, draftQuantityOf, itemsForPhase, soloLabel } from "@buildpy/core/modules/process-standard/draft";
import type { ProcessStandardItem } from "@buildpy/core/modules/process-standard/types";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";

/** 공종 코드 → 그 공종이 속한 공정. 서버가 공종 표에서 뽑아 준다. */
export interface TradePhaseRef {
  trade_code: string;
  phase_code: string | null;
}

export interface PhaseOption {
  code: string;
  name: string;
  /** 시공 순서. 작을수록 먼저. */
  sort_order: number;
}

// 면적 초안에 쓰는 공정 표준 판 — 견적에 고정된 판, 없으면 최신 판.
export interface ScheduleStandard {
  version: { id: string; label: string; version: number };
  items: ProcessStandardItem[];
}

const NO_CALIBRATION: ProcessCalibration = { crewSize: null, factor: 1 };

interface Props {
  schedule: EditorSchedule;
  /** 지금 견적에 들어 있는 공종. 공정 줄이 여기서 파생된다. */
  trades: EditorTrade[];
  phases: PhaseOption[];
  tradePhases: TradePhaseRef[];
  dispatch: Dispatch<EditorAction>;
  standard?: ScheduleStandard | null;
  calibration?: ProcessCalibration;
  // 견적 면적(m²) — 그 공정에 ㎡ 줄이 없을 때 초안 수량.
  areaM2?: number | null;
  // 잠긴 견적 — 일수·날짜·시공법·초안 단추를 읽기 전용으로.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 응답이 markLocked 직후·재렌더 전에 와도 버린다.
  isLocked: () => boolean;
  // 조상 fieldset 이 이미 잠긴 안내를 가리키면 true — 안내는 가장 바깥 fieldset 에만 단다.
  lockNotedAbove?: boolean;
}

interface Row {
  phase_code: string;
  phase_name: string;
  /** 이 공정에 묶인 공종 이름들. */
  trade_names: string[];
  duration_days: number;
  start_date: string;
  end_date: string;
  // 이 공정의 표준 시공법 후보 — 비면 시공법 칸을 안 보인다.
  methods: ProcessStandardItem[];
  plan: EditorPhasePlan | null;
}

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"] as const;

/** `2026-03-05` → `3/5(목)`. 형식이 아니면 그대로 돌려준다. */
const shortDate = (iso: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [y, m, d] = iso.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${m}/${d}(${WEEKDAY[dow]})`;
};

const DAY_MS = 86_400_000;

/** 두 날짜 사이의 달력일 수(양끝 포함). 막대 길이를 그릴 때만 쓴다. */
const spanDays = (from: string, to: string): number => {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.floor((b - a) / DAY_MS) + 1;
};

const PhaseBar = ({
  offset,
  length,
  total,
}: {
  offset: number;
  length: number;
  total: number;
}) => (
  <div
    aria-hidden
    className="relative h-2 min-w-24 flex-1 rounded-md bg-muted"
    title={`${length}일`}>
    <div
      className="absolute inset-y-0 left-(--bar-left) w-(--bar-w) rounded-md bg-primary/70"
      style={
        {
          "--bar-left": `${total > 0 ? (offset / total) * 100 : 0}%`,
          "--bar-w": `${total > 0 ? Math.max((length / total) * 100, 2) : 0}%`,
        } as CSSProperties
      }
    />
  </div>
);

// 시공법 고르기 + 초안 근거 한 줄(「N일(표준 K명) · 기능공 1명이면 M일」 · 근거 팝오버).
const MethodCell = ({
  methods,
  value,
  phaseName,
  basis,
  draftDays,
  onChange,
  disabled = false,
}: {
  methods: ProcessStandardItem[];
  value: string;
  phaseName: string;
  basis: ReturnType<typeof draftOfItem> | null;
  draftDays: number | null;
  onChange: (code: string) => void;
  disabled?: boolean;
}) => (
  <div className="flex flex-col gap-y-1">
    <SimpleSelect
      aria-label={`${phaseName} 시공법`}
      className="w-40"
      disabled={disabled}
      options={methods.map((m) => ({ value: m.code, label: m.name }))}
      size="sm"
      value={value}
      onValueChange={onChange}
    />
    {basis && draftDays !== null && (
      <div className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
        <span>
          {draftDays}일({basis.crew_size === basis.standard_crew_size ? "표준" : "회사"} {crewLabel(basis.crew_size, basis.laborer_size)}) · {soloLabel(basis.laborer_size)} {basis.solo_days}일
        </span>
        <InfoTip label={`${phaseName} 초안 근거`}>
          {basis.item_name} · {basis.formula}
          <span className="mt-1 block text-muted-foreground">출처: {basis.source}</span>
        </InfoTip>
      </div>
    )}
  </div>
);

const SchedulePanel = ({
  schedule,
  trades,
  phases,
  tradePhases,
  dispatch: rawDispatch,
  standard = null,
  calibration = NO_CALIBRATION,
  areaM2 = null,
  disabled = false,
  isLocked,
  lockNotedAbove = false,
}: Props) => {
  // 칸·초안·날짜 채우기가 보내는 액션은 보내는 순간 잠겨 있으면 버린다(재렌더 전 잠김 포함).
  const dispatch = useMemo(() => unlessLocked(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 펼쳐 둔 날짜를 덮어쓰기 전에 띄우는 확인.
  const [askRefill, setAskRefill] = useState(false);
  // 면적 초안이 정한 일수로 날짜를 다시 채울 때 확인 뒤에 쓸 공정 목록.
  const [pending, setPending] = useState<{ phase_code: string; duration_days: number }[] | null>(null);
  const [draftNote, setDraftNote] = useState<string | null>(null);

  /**
   * 화면에 설 공정 줄. **상태가 아니라 파생값이다** — 공종을 넣고 빼면 줄이
   * 따라 늘고 줄어야 하는데, 상태에 박아 두면 그때마다 줄을 맞춰 주는 코드가
   * 필요하고 그 코드가 틀리면 일수가 엉뚱한 공정으로 옮겨 붙는다.
   */
  const rows = useMemo<Row[]>(() => {
    const phaseOf = new Map(
      tradePhases.map((t) => [t.trade_code, t.phase_code])
    );
    const meta = new Map(phases.map((p) => [p.code, p]));
    const planOf = new Map(schedule.plans.map((p) => [p.phase_code, p]));

    const namesByPhase = new Map<string, string[]>();
    for (const trade of trades) {
      const code = phaseOf.get(trade.trade);
      // 공정을 안 정한 공종은 일정에 세우지 않는다. 설정 화면에서 공정을
      // 지정하면 그때 줄이 선다.
      if (!code) continue;
      const names = namesByPhase.get(code) ?? [];
      names.push(trade.name.trim() || trade.trade);
      namesByPhase.set(code, names);
    }

    return [...namesByPhase.entries()]
      .map(([code, names]) => {
        const plan = planOf.get(code);
        return {
          phase_code: code,
          phase_name: meta.get(code)?.name ?? code,
          trade_names: names,
          duration_days: plan?.duration_days ?? 0,
          start_date: plan?.start_date ?? "",
          end_date: plan?.end_date ?? "",
          methods: standard ? itemsForPhase(standard.items, code) : [],
          plan: plan ?? null,
        };
      })
      .sort(
        (a, b) =>
          (meta.get(a.phase_code)?.sort_order ?? 0) -
            (meta.get(b.phase_code)?.sort_order ?? 0) ||
          a.phase_name.localeCompare(b.phase_name)
      );
  }, [trades, phases, tradePhases, schedule.plans, standard]);

  // 초안 수량을 셀 견적 줄(윗줄만 — 세트의 하위 줄은 윗줄 수량에 이미 들어 있다).
  const lines = useMemo<DraftLine[]>(() => {
    const phaseOf = new Map(tradePhases.map((t) => [t.trade_code, t.phase_code]));
    return trades.flatMap((t) => t.items.map((i) => ({ phase_code: phaseOf.get(t.trade) ?? null, unit: i.unit, qty: i.qty })));
  }, [trades, tradePhases]);

  const draftRows = rows.filter((r) => r.methods.length > 0);
  const draftable = draftRows.filter((r) => draftQuantityOf(lines, areaM2, r.phase_code) !== null);
  const draftBlocked =
    draftRows.length === 0
      ? "표준 시공법이 있는 공정이 없습니다"
      : draftable.length === 0
        ? "면적이나 ㎡ 단위 줄이 있어야 초안을 낼 수 있습니다"
        : null;

  const dated = rows.filter((r) => r.start_date && r.end_date);
  /**
   * 날짜 열을 보여 줄지. **`dated` 로 가르면 안 된다** — 줄이 하나뿐인 견적에서
   * 한쪽 날짜를 지우는 순간 열이 통째로 사라져, 남아 있는 다른 쪽을 고칠 방법이
   * 「날짜 다시 채우기」(전부 덮어씀)뿐이 된다.
   */
  const hasAnyDate = rows.some((r) => r.start_date || r.end_date);
  const spanStart = dated.length
    ? dated.reduce((min, r) => (r.start_date < min ? r.start_date : min), dated[0].start_date)
    : "";
  const spanEnd = dated.length
    ? dated.reduce((max, r) => (r.end_date > max ? r.end_date : max), dated[0].end_date)
    : "";
  const totalSpan = dated.length ? spanDays(spanStart, spanEnd) : 0;

  const filled = rows.filter((r) => r.duration_days >= 1);
  const canGenerate =
    !busy && filled.length > 0 && schedule.expected_start_date !== "";

  const onGenerate = () => {
    // 이미 펼쳐 둔 날짜를 손으로 고쳤다면 여기서 덮인다. 한 번 묻는다.
    if (dated.length > 0) {
      setAskRefill(true);
      return;
    }
    void generate();
  };

  const generate = async (override?: { phase_code: string; duration_days: number }[]) => {
    setBusy(true);
    setError(null);
    setPending(null);
    try {
      const result = await generateQuoteSchedule({
        start_date: schedule.expected_start_date,
        work_saturday: schedule.work_saturday,
        work_sunday: schedule.work_sunday,
        work_holiday: schedule.work_holiday,
        phases:
          override ??
          filled.map((r) => ({
            phase_code: r.phase_code,
            duration_days: r.duration_days,
          })),
      });
      if (isLocked()) return;
      if (!result.ok) {
        setError(result.message);
        return;
      }
      dispatch({
        type: "APPLY_SCHEDULE",
        phases: result.phases,
        holidays: result.holidays,
      });
    } catch (e) {
      // 서버 액션이 통째로 거부되는 경우(네트워크·처리 안 된 예외)가 있다.
      // 잡지 않으면 버튼만 풀리고 **아무 말 없이 아무 일도 안 일어난다.**
      setError(
        e instanceof Error ? e.message : "날짜를 채우지 못했습니다"
      );
    } finally {
      setBusy(false);
    }
  };

  // 면적으로 일수를 채우고, 착공일이 있으면 이어서 날짜까지 채운다(손댄 날짜가 있으면 묻는다).
  const onDraft = () => {
    if (!standard || draftBlocked) return;
    const drafts = draftRows
      .map((r) => draftPhase({ items: standard.items, lines, areaM2, calibration, phase_code: r.phase_code, item_code: r.plan?.standard_item_code ?? null }))
      .filter((d) => d !== null);
    dispatch({
      type: "APPLY_PHASE_DRAFTS",
      version_id: standard.version.id,
      drafts: drafts.map((d) => ({
        phase_code: d.phase_code,
        standard_item_code: d.item_code,
        quantity_m2: d.quantity_m2,
        crew_size: d.crew_size,
        draft_days: d.crew_days,
        duration_days: d.duration_days,
      })),
    });
    const daysOf = new Map(drafts.map((d) => [d.phase_code, d.duration_days]));
    const next = rows
      .map((r) => ({ phase_code: r.phase_code, duration_days: daysOf.get(r.phase_code) ?? r.duration_days }))
      .filter((r) => r.duration_days >= 1);
    if (schedule.expected_start_date === "") {
      setDraftNote("일수를 채웠습니다 — 예상 착공일을 넣고 「날짜 채우기」 를 누르면 날짜가 들어갑니다.");
      return;
    }
    setDraftNote(null);
    if (dated.length > 0) {
      setPending(next);
      setAskRefill(true);
      return;
    }
    void generate(next);
  };

  // 저장해 둔 초안 근거를 지금 판·계수로 다시 풀어 보인다 — 손으로 일수를 고쳤으면 근거가 비어 있다.
  const basisOf = (row: Row) => {
    const plan = row.plan;
    if (!plan?.draft_days || !plan.quantity_m2 || !plan.crew_size) return null;
    const item = row.methods.find((m) => m.code === plan.standard_item_code);
    return item ? draftOfItem(item, plan.quantity_m2, plan.crew_size, calibration.factor) : null;
  };
  const showMethods = draftRows.length > 0;

  if (rows.length === 0) {
    return (
      <SectionCard as="div" padding="sm">
        <div className="pb-1 text-sm font-medium">시공일정</div>
        <p className="text-xs text-muted-foreground">
          공종에 공정이 지정되면 여기에 줄이 섭니다. 설정 → 공종에서 정할 수
          있습니다.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard as="div" padding="sm">
      <fieldset aria-describedby={lockNotedAbove ? undefined : lockedDescribedBy(disabled)} className="min-w-0" data-testid="schedule-panel-body" disabled={disabled}>
      <div className="flex items-center justify-between pb-1">
        <span className="text-sm font-medium">시공일정</span>
        <span className="text-xs text-muted-foreground">선택</span>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-1 font-normal">공정</th>
            <th className="py-1 font-normal">포함 공종</th>
            {showMethods && <th className="py-1 font-normal">시공법</th>}
            <th className="w-20 py-1 text-right font-normal">며칠</th>
            {hasAnyDate && <th className="py-1 font-normal">기간</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.phase_code} className="border-t border-border">
              <td className="py-1 whitespace-nowrap">{row.phase_name}</td>
              <td className="py-1 text-xs text-muted-foreground">
                {row.trade_names.join(", ")}
              </td>
              {showMethods && (
                <td className="py-1">
                  {row.methods.length > 0 && (
                    <MethodCell
                      basis={basisOf(row)}
                      disabled={disabled}
                      draftDays={row.plan?.draft_days ?? null}
                      methods={row.methods}
                      phaseName={row.phase_name}
                      value={row.plan?.standard_item_code ?? row.methods[0].code}
                      onChange={(code) => dispatch({ type: "SET_PHASE_METHOD", phase_code: row.phase_code, item_code: code })}
                    />
                  )}
                </td>
              )}
              <td className="py-1 text-right">
                <NumberField
                  ariaLabel={`${row.phase_name} 작업일수`}
                  className="w-16 text-right phone:min-h-10"
                  disabled={disabled}
                  value={row.duration_days}
                  variant="outline"
                  onCommit={(value) =>
                    dispatch({
                      type: "SET_PHASE_DURATION",
                      phase_code: row.phase_code,
                      value,
                    })
                  }
                />
              </td>
              {hasAnyDate && (
                <td className="py-1">
                  {/*
                    **한쪽만 비어도 칸을 계속 보여 준다.** 「둘 다 있을 때만」
                    으로 두면 시작일을 지우는 순간 두 칸이 모두 「—」 로 바뀌어,
                    남아 있는 종료일을 고칠 방법이 「날짜 다시 채우기」(전부
                    덮어씀)뿐이 된다.
                  */}
                  {row.duration_days >= 1 ? (
                    <div className="flex items-center gap-x-2">
                      <Input
                        aria-label={`${row.phase_name} 시작일`}
                        className="h-8 w-36"
                        disabled={disabled}
                        name={`phase-start-${row.phase_code}`}
                        type="date"
                        value={row.start_date}
                        onChange={(e) =>
                          dispatch({
                            type: "SET_PHASE_DATE",
                            phase_code: row.phase_code,
                            field: "start_date",
                            value: e.target.value,
                          })
                        }
                      />
                      <Input
                        aria-label={`${row.phase_name} 종료일`}
                        className="h-8 w-36"
                        disabled={disabled}
                        name={`phase-end-${row.phase_code}`}
                        type="date"
                        value={row.end_date}
                        onChange={(e) =>
                          dispatch({
                            type: "SET_PHASE_DATE",
                            phase_code: row.phase_code,
                            field: "end_date",
                            value: e.target.value,
                          })
                        }
                      />
                      {row.start_date && row.end_date && (
                        <PhaseBar
                          length={spanDays(row.start_date, row.end_date)}
                          offset={spanDays(spanStart, row.start_date) - 1}
                          total={totalSpan}
                        />
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-2">
        <label className="flex items-center gap-x-2 text-sm">
          <span className="whitespace-nowrap text-muted-foreground">
            예상 착공일
          </span>
          <Input
            className="h-8 w-40"
            disabled={disabled}
            name="expected-start-date"
            type="date"
            value={schedule.expected_start_date}
            onChange={(e) =>
              dispatch({ type: "SET_SCHEDULE_START", value: e.target.value })
            }
          />
        </label>

        <div className="flex items-center gap-x-3">
          <span className="text-sm whitespace-nowrap text-muted-foreground">
            작업 포함
          </span>
          {(
            [
              ["work_saturday", "토요일"],
              ["work_sunday", "일요일"],
              ["work_holiday", "공휴일"],
            ] as const
          ).map(([field, label]) => (
            <label key={field} className="flex items-center gap-x-1 text-sm">
              <Checkbox
                checked={schedule[field]}
                disabled={disabled}
                onCheckedChange={() =>
                  dispatch({ type: "TOGGLE_WORKDAY", field })
                }
              />
              {label}
            </label>
          ))}
        </div>

        {standard && (
          <Button
            disabled={disabled || busy || draftBlocked !== null}
            size="sm"
            title={draftBlocked ?? undefined}
            variant="outline"
            onClick={onDraft}>
            면적으로 초안
          </Button>
        )}

        <Button
          disabled={disabled || !canGenerate}
          size="sm"
          variant="outline"
          onClick={onGenerate}>
          {busy
            ? "채우는 중…"
            : dated.length > 0
              ? "날짜 다시 채우기"
              : "날짜 채우기"}
        </Button>
      </div>

      {dated.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">
          {shortDate(spanStart)} ~ {shortDate(spanEnd)}
          {schedule.holidays === null && !schedule.work_holiday && (
            <span className="ml-2 text-warning">공휴일 미반영</span>
          )}
        </p>
      )}

      <p className="pt-1 text-xs text-muted-foreground">
        {error ? (
          <span className="text-destructive">{error}</span>
        ) : draftNote ? (
          draftNote
        ) : standard && draftBlocked && draftRows.length > 0 ? (
          `면적으로 초안 — ${draftBlocked}`
        ) : (
          "공정별 일수를 넣고 「날짜 채우기」 를 누르면 시작·종료일이 들어갑니다. 채운 뒤 날짜를 직접 고칠 수 있습니다."
        )}
      </p>
      <ConfirmDialog
        confirmLabel="다시 채우기"
        description="직접 고친 날짜가 있으면 새로 채운 날짜로 덮어씁니다."
        open={openUnlessLocked(askRefill, disabled)}
        testId="schedule-refill-confirm"
        title="날짜를 다시 채울까요?"
        onConfirm={unlessLocked(lockedNow(disabled, isLocked), () => void generate(pending ?? undefined))}
        onOpenChange={(open) => {
          setAskRefill(open);
          if (!open) setPending(null);
        }}
      />
      </fieldset>
    </SectionCard>
  );
};

export default SchedulePanel;
